import { createRef } from '@wix/materializer'
import type { RenderFlags } from '@wix/thunderbolt-becky-types'
import type { CompNode } from '@wix/thunderbolt-catharsis'
import { createCompNode, envRefs } from '@wix/thunderbolt-catharsis'
import type { DefaultProperties } from '@wix/thunderbolt-components-native'

const renderFlagRef = <T extends keyof RenderFlags>(renderFlag: T) =>
	createRef<RenderFlags[T]>(['environment', 'renderFlags', renderFlag])

const renderFlags = [
	'componentViewMode',
	'isBackToTopButtonAllowed',
	'shouldBlockGoogleMapInteraction',
	'shouldResetTinyMenuZIndex',
	'shouldResetSlideShowNextPrevButtonsPosition',
	'shouldResetSubscribeFormMinMaxWidth',
	'isPlayingAllowed',
	'isSocialInteractionAllowed',
	'isExternalNavigationAllowed',
	'isSlideShowGalleryClickAllowed',
	'renderFixedPositionContainers',
	'shouldResetGalleryToOriginalState',
	'shouldResetComponent',
	'isComponentTransitionAllowed',
	'renderFixedPositionBackgrounds',
	'isWixBlocks',
	'isAdi',
	'isLivePreviewOpenForEditor',
] as const

type RenderFlagsCompInfos = { [key in typeof renderFlags[number]]: CompNode<any, RenderFlags[key], any> }

const renderFlagsCompNodes = renderFlags.reduce((acc, flag) => {
	acc[flag] = createCompNode({
		getDependencies: () => createRef<any>(['environment', 'renderFlags', flag]),
		toViewItem: (__, data) => data,
	})
	return acc
}, {} as RenderFlagsCompInfos)

const compPreviewState = createCompNode({
	getDependencies: (component) =>
		createRef<boolean>(['environment', 'renderFlags', 'componentPreviewStates', component.id]),
	toViewItem: (__, data) => data,
})
const compVisibility = createCompNode({
	getDependencies: (component, refs) => {
		return {
			ignoreComponentsHiddenProperty: renderFlagRef('ignoreComponentsHiddenProperty'),
			ignoreComponentsCollapsedProperty: renderFlagRef('ignoreComponentsCollapsedProperty'),
			props: refs.propertyQuery<DefaultProperties>(component.propertyQuery!),
		}
	},
	toViewItem: (component, data) => {
		const { ignoreComponentsHiddenProperty, ignoreComponentsCollapsedProperty, props } = data
		return {
			isHidden: !!props.isHidden && !ignoreComponentsHiddenProperty.includes(component.id),
			isCollapsed: !!props.isCollapsed && !ignoreComponentsCollapsedProperty.includes(component.id),
		}
	},
})

const activeVariant = createCompNode({
	getDependencies: () => envRefs.activeVariant,
	toViewItem: (__, data) => data,
})

export const previewCompInfoNodes = {
	compPreviewState,
	compVisibility,
	activeVariant,
	...renderFlagsCompNodes,
} as const
